import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, CheckCircle } from 'lucide-react';

/**
 * PWA Install Prompt Component
 *
 * Shows an "Install App" button when the app is installable.
 * Only appears when:
 * 1. Browser supports PWA installation
 * 2. App meets PWA criteria (manifest, service worker, HTTPS)
 * 3. App is not already installed
 */
export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installStatus, setInstallStatus] = useState('not-installable');

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('[PWA] App is already running in standalone mode');
      setIsInstalled(true);
      setInstallStatus('installed');
      return;
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('[PWA] beforeinstallprompt event fired!');
      console.log('[PWA] App is installable!');

      // Prevent the default browser prompt
      e.preventDefault();

      // Store the event for later use
      setDeferredPrompt(e);
      setShowPrompt(true);
      setInstallStatus('installable');

      console.log('[PWA] Install prompt captured. User can now install via button.');
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      console.log('[PWA] App was successfully installed!');
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
      setInstallStatus('installed');
    };

    // Add event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Debug: Log current state
    console.log('[PWA] PWAInstallPrompt component mounted');
    console.log('[PWA] Checking installation status...');

    // Check if service worker is registered
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        console.log('[PWA] Service Worker is ready:', registration);
      });
    } else {
      console.warn('[PWA] Service Worker not supported in this browser');
      setInstallStatus('not-supported');
    }

    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Handle install button click
  const handleInstallClick = async () => {
    console.log('[PWA] Install button clicked');

    if (!deferredPrompt) {
      console.error('[PWA] No deferred prompt available');
      alert('Installation prompt not available. Please try using browser menu to install.');
      return;
    }

    console.log('[PWA] Showing install prompt to user...');

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    console.log(`[PWA] User response: ${outcome}`);

    if (outcome === 'accepted') {
      console.log('[PWA] User accepted the install prompt');
      setShowPrompt(false);
    } else {
      console.log('[PWA] User dismissed the install prompt');
    }

    // Clear the deferred prompt
    setDeferredPrompt(null);
  };

  // Handle dismiss button
  const handleDismiss = () => {
    console.log('[PWA] User dismissed install prompt');
    setShowPrompt(false);
  };

  // Don't show anything if already installed
  if (isInstalled) {
    return (
      <div className="fixed top-4 right-4 bg-green-50 border border-green-200 rounded-lg p-3 shadow-lg z-50">
        <div className="flex items-center gap-2 text-sm text-green-700">
          <CheckCircle className="h-4 w-4" />
          <span>App Installed</span>
        </div>
      </div>
    );
  }

  // Don't show prompt if not installable
  if (!showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm bg-white border-2 border-blue-500 rounded-lg shadow-2xl p-4 z-50 animate-slide-up">
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
        aria-label="Dismiss"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="flex items-start gap-3">
        <div className="bg-blue-100 rounded-lg p-2">
          <Download className="h-6 w-6 text-blue-600" />
        </div>

        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1">
            Install QA Audit App
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Install this app on your device for quick and easy access when you're on the go.
          </p>

          <div className="flex gap-2">
            <Button
              onClick={handleInstallClick}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              size="sm"
            >
              <Download className="h-4 w-4 mr-2" />
              Install
            </Button>
            <Button
              onClick={handleDismiss}
              variant="outline"
              size="sm"
            >
              Not Now
            </Button>
          </div>
        </div>
      </div>

      {/* Debug info (remove in production) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
          <div>Status: {installStatus}</div>
          <div>Deferred Prompt: {deferredPrompt ? 'Available' : 'Not available'}</div>
        </div>
      )}
    </div>
  );
}

// Add CSS animation
const style = document.createElement('style');
style.textContent = `
  @keyframes slide-up {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .animate-slide-up {
    animation: slide-up 0.3s ease-out;
  }
`;
document.head.appendChild(style);
